import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';

// Backend server URL - adjust if needed
const SOCKET_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

// Store socket instance outside component to ensure singleton
let socketInstance = null;

export const useReportNotifications = () => {
  const isConnected = useRef(false);

  useEffect(() => {
    // Only create socket if it doesn't exist
    if (!socketInstance) {
      socketInstance = io(SOCKET_URL, {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });
    }

    const socket = socketInstance;

    // Prevent duplicate event listeners
    socket.off('connect');
    socket.off('disconnect');
    socket.off('connect_error');
    socket.off('notification');
    socket.off('pdfStatus');
    socket.off('reportStatus');
    socket.off('patientAdded');

    // Connection events
    socket.on('connect', () => {
      if (!isConnected.current) {
        console.log('✅ Connected to notification server');
        isConnected.current = true;
      }
      try {
        const raw = localStorage.getItem('user');
        const user = raw ? JSON.parse(raw) : null;
        const userId = user?._id || user?.id || user?.userId;
        if (userId) {
          socket.emit('join', { userId: String(userId) });
        }
      } catch (e) {
        console.warn('Could not join user notification room', e);
      }
    });

    socket.on('disconnect', () => {
      console.log('❌ Disconnected from notification server');
      isConnected.current = false;
    });

    socket.on('connect_error', (error) => {
      console.error('Connection error:', error);
    });

    // Listen for general notifications
    socket.on('notification', (notification) => {
      console.log('📬 Notification received:', notification);
      window.dispatchEvent(new CustomEvent('peleescribe:notification', { detail: notification }));

      const message = notification.message || notification.title;

      switch (notification.type) {
        case 'success':
          toast.success(message, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          break;

        case 'error':
          // Error alerts removed - no user-facing notifications
          console.error('Error notification received (not displayed):', notification);
          break;

        case 'warning':
          toast.warning(message, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          break;

        case 'info':
        default:
          toast.info(message, {
            position: 'top-right',
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });
          break;
      }
    });

    // Listen for PDF generation status
    socket.on('pdfStatus', (status) => {
      console.log('📄 PDF Status Update:', status);

      if (status.type === 'start') {
        toast.info(
          `📄 Creating PDF report for ${status.patientName || 'patient'}...`,
          {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      } else if (status.type === 'complete') {
        toast.success(
          `✅ PDF report generated successfully for ${status.patientName || 'patient'}!`,
          {
            position: 'top-right',
            autoClose: 6000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      } else if (status.type === 'error') {
        // Error alerts removed - no user-facing notifications
        console.error('PDF generation error (not displayed):', status);
      }
    });

    // Listen for report generation status
    socket.on('reportStatus', (status) => {
      console.log('📋 Report Status Update:', status);

      if (status.type === 'start') {
        toast.info(
          `📋 Starting report generation for ${status.patientName || 'patient'}...`,
          {
            position: 'top-right',
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      } else if (status.type === 'generating') {
        toast.info(
          `🤖 Claude AI is analyzing patient data for ${status.patientName || 'patient'}...`,
          {
            position: 'top-right',
            autoClose: 4000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      } else if (status.type === 'complete') {
        toast.success(
          `🎉 Report completed for ${status.patientName || 'patient'}! Ready to view.`,
          {
            position: 'top-right',
            autoClose: 6000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      } else if (status.type === 'error') {
        // Error alerts removed - no user-facing notifications
        console.error('Report generation error (not displayed):', status);
      }
    });

    // Listen for patient added event
    socket.on('patientAdded', (data) => {
      console.log('👤 Patient Added:', data);
      toast.success(
        `✅ Patient ${data.patientName || 'record'} added successfully!`,
        {
          position: 'top-right',
          autoClose: 5000,
        }
      );
    });

    // Cleanup on component unmount - don't disconnect the singleton socket
    return () => {
      // Remove event listeners but don't disconnect the socket
      // This prevents duplicate listeners while maintaining the connection
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('notification');
      socket.off('pdfStatus');
      socket.off('reportStatus');
      socket.off('patientAdded');
    };
  }, []); // Empty dependency array - runs once on mount

  return null; // This hook doesn't return anything
};
